// src/components/Broadcast/Broadcast.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Row, Col, Button, Form, Card, Modal,  Spinner } from 'react-bootstrap';
import { showTemplates, showContacts, getContactGroups, CreateBroadcast, getCompany } from '../../../helpers/api';
import AddNewTemplate from '../Templates/AddNewTemplate';
import '../Templates/AddNewTemplate.css';
import './NewBroadcast.css';
import ShreyuDatepicker from '../../../components/Datepicker';
// import Table from '../../../components/Table';
import { Table } from 'react-bootstrap';

export interface Template {
    template_id: string;
    template_name: string;
    title_type: string;
    title_text: string;
    title_file: string;
    header_variables?: string;
    body?: string;
    footer?: string;
    template_buttons?: Array<{
        type: string;
        text: string;
        url?: string;
        phone_number?: string;
    }>;
}
interface BroadcastData {
    broadcast_name: string;
    template_id: string;
    type: string;
    contacts?: string[];
    select_all?: boolean;
    search?: string;
    group?: string;
    exclude?: string[];
    schedule_date?: string | null;
    schedule_time?: string | null;
}
export interface Contact {
    contact_id: string;
    contact_name: string;
    mobile_no: string;
    group_name: string;
}

/**
 * Who receives the broadcast. `manual` is an explicit set of ticked contacts; `filter` means
 * "everyone matching this search/group" (resolved on the server), minus any unticked exceptions.
 * This keeps the browser from ever holding or posting 100k contacts.
 */
type Audience =
    | { mode: 'manual'; ids: Set<string> }
    | { mode: 'filter'; search: string; group: string; total: number; excluded: Set<string> };

const PICKER_PAGE_SIZE = 12;
const SEARCH_DEBOUNCE_MS = 350;

const Broadcast: React.FC = () => {
    // =================== STATES ===================
    const [broadcastName, setBroadcastName] = useState('');
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [audience, setAudience] = useState<Audience>({ mode: 'manual', ids: new Set() });
    const [groups, setGroups] = useState<{ name: string; total: number }[]>([]);
    const [totalContacts, setTotalContacts] = useState(0);
    const [templates, setTemplates] = useState<Template[]>([]);

    // Contact picker (server-paged)
    const [pickerRows, setPickerRows] = useState<Contact[]>([]);
    const [pickerLoading, setPickerLoading] = useState(false);
    const [pickerPage, setPickerPage] = useState(1);
    const [pickerLastPage, setPickerLastPage] = useState(1);
    const [pickerTotal, setPickerTotal] = useState(0);
    const [pickerSearchInput, setPickerSearchInput] = useState('');
    const [pickerSearch, setPickerSearch] = useState('');
    const [pickerGroup, setPickerGroup] = useState('');
    const pickerRequest = useRef(0);
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [showContactModal, setShowContactModal] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [showTemplatePreview, setShowTemplatePreview] = useState(false);
    const [contactOption, setContactOption] = useState<'group' | 'individual'>('group');
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dateOption, setDateOption] = useState<'non-schedule' | 'schedule'>('non-schedule');
    const [scheduleDate, setScheduleDate] = useState<Date | undefined>(undefined);
    const [scheduleTime, setScheduleTime] = useState('09:00');
    const [companyName, setCompanyName] = useState<string>('Loading...');
    const [profile, setProfile] = useState<string>('Loading...');
    const [companyId, setCompanyId] = useState<string>('');
    // Optional media the user picks for a template whose header is image/video/document.
    const [headerFile, setHeaderFile] = useState<File | null>(null);
    const [headerFileUrl, setHeaderFileUrl] = useState<string>('');

    // =================== FETCH DATA ===================
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);

                const [templatesResponse, groupsResponse] = await Promise.all([
                    showTemplates(),
                    getContactGroups()
                ]);

                setTemplates(templatesResponse.data.templates);
                setGroups(groupsResponse.data?.groups ?? []);
                setTotalContacts(groupsResponse.data?.total ?? 0);
            } catch (error) {
                console.error('Error fetching data:', error);
                setError('Failed to load data. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);
    const company_id = JSON.parse(sessionStorage.getItem('shreyu_user') || '{}')?.user?.company_id;

    useEffect(() => {
        const fetchCompany = async () => {
            try {
                const response = await getCompany(company_id);
                setCompanyName(response.data.company_name || 'Company Name');
                setProfile(response.data.logo || 'profile');
                // Company is workspace-scoped; capture its id so template media URLs resolve
                // (session company_id is usually null).
                const cid = response.data?.company_id ?? response.data?.company?.company_id ?? company_id;
                if (cid) setCompanyId(String(cid));
            } catch (err) {
                console.error('Failed to fetch company:', err);
                setCompanyName('Company Name');
            }
        };

        fetchCompany();
    }, []);
    // =================== HELPERS ===================
    const getSelectedTemplateDetails = (): Template | undefined =>
        templates.find(t => t.template_id.toString() === selectedTemplate.toString());

    const handleTemplateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const templateId = e.target.value;
        setSelectedTemplate(templateId);
        setShowTemplatePreview(!!templateId);
        // A new template means any previously picked header file no longer applies.
        setHeaderFile(null);
        setHeaderFileUrl('');
    };

    const onHeaderFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        if (f) {
            setHeaderFile(f);
            setHeaderFileUrl(URL.createObjectURL(f));
        }
    };

    // =================== AUDIENCE ===================
    // While an "everyone matching…" audience is active the picker is locked to that filter,
    // so ticked/unticked rows always reflect the real audience.
    const effectiveSearch = audience.mode === 'filter' ? audience.search : pickerSearch;
    const effectiveGroup = audience.mode === 'filter' ? audience.group : pickerGroup;

    const fetchPicker = useCallback(async () => {
        const id = ++pickerRequest.current;
        setPickerLoading(true);
        try {
            const res = await showContacts({
                page: pickerPage,
                per_page: PICKER_PAGE_SIZE,
                search: effectiveSearch || undefined,
                group: effectiveGroup || undefined,
            });
            if (id !== pickerRequest.current) return;

            setPickerRows(
                (res.data?.data ?? []).map((c: any): Contact => ({
                    contact_id: String(c.contact_id),
                    contact_name: c.contact_name || '',
                    mobile_no: String(c.mobile_no ?? ''),
                    group_name: c.group_name || 'Ungrouped',
                }))
            );
            setPickerTotal(res.data?.meta?.total ?? 0);
            setPickerLastPage(res.data?.meta?.last_page ?? 1);
        } catch (err) {
            if (id === pickerRequest.current) console.error('Failed to load contacts:', err);
        } finally {
            if (id === pickerRequest.current) setPickerLoading(false);
        }
    }, [pickerPage, effectiveSearch, effectiveGroup]);

    useEffect(() => {
        if (showContactModal) fetchPicker();
    }, [showContactModal, fetchPicker]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setPickerSearch(pickerSearchInput.trim());
            setPickerPage(1);
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [pickerSearchInput]);

    const isChecked = (id: string) =>
        audience.mode === 'manual' ? audience.ids.has(id) : !audience.excluded.has(id);

    const toggleContact = (id: string) =>
        setAudience(a => {
            if (a.mode === 'manual') {
                const ids = new Set(a.ids);
                if (ids.has(id)) ids.delete(id);
                else ids.add(id);
                return { ...a, ids };
            }
            const excluded = new Set(a.excluded);
            if (excluded.has(id)) excluded.delete(id);
            else excluded.add(id);
            return { ...a, excluded };
        });

    const allOnPageChecked = pickerRows.length > 0 && pickerRows.every(c => isChecked(c.contact_id));

    const togglePage = (checked: boolean) =>
        setAudience(a => {
            if (a.mode === 'manual') {
                const ids = new Set(a.ids);
                pickerRows.forEach(c => (checked ? ids.add(c.contact_id) : ids.delete(c.contact_id)));
                return { ...a, ids };
            }
            const excluded = new Set(a.excluded);
            pickerRows.forEach(c => (checked ? excluded.delete(c.contact_id) : excluded.add(c.contact_id)));
            return { ...a, excluded };
        });

    const selectAllMatching = () =>
        setAudience({ mode: 'filter', search: pickerSearch, group: pickerGroup, total: pickerTotal, excluded: new Set() });

    const clearAudience = () => setAudience({ mode: 'manual', ids: new Set() });

    // Choose a whole group (or everyone when name is ''); choosing it again clears it.
    const selectGroup = (name: string, total: number) =>
        setAudience(a =>
            a.mode === 'filter' && a.group === name && !a.search && a.excluded.size === 0
                ? { mode: 'manual', ids: new Set() }
                : { mode: 'filter', search: '', group: name, total, excluded: new Set() }
        );

    const isGroupActive = (name: string) =>
        audience.mode === 'filter' && audience.group === name && !audience.search && audience.excluded.size === 0;

    const selectedCount =
        audience.mode === 'manual' ? audience.ids.size : Math.max(0, audience.total - audience.excluded.size);

    const audienceLabel =
        audience.mode === 'filter'
            ? [audience.group ? `group “${audience.group}”` : 'all contacts', audience.search ? `matching “${audience.search}”` : '']
                  .filter(Boolean)
                  .join(' ')
            : 'hand-picked contacts';

    // =================== SUBMIT HANDLER ===================
    const handleSubmit = async () => {
        if (!broadcastName || !selectedTemplate || selectedCount === 0) {
            alert('Please fill all required fields: Broadcast Name, Template, and at least one contact');
            return;
        }

        // Format the picked date as a LOCAL YYYY-MM-DD. Using toISOString() here
        // converted to UTC and could shift the date a day earlier (e.g. IST), so the
        // broadcast fired on the wrong day. The backend combines it with the time via
        // MySQL TIMESTAMP(schedule_date, schedule_time), so both must be local values.
        const pad = (n: number) => String(n).padStart(2, '0');
        const schedule_time = dateOption === "schedule" ? scheduleTime : null;
        const schedule_date =
            dateOption === "schedule" && scheduleDate
                ? `${scheduleDate.getFullYear()}-${pad(scheduleDate.getMonth() + 1)}-${pad(scheduleDate.getDate())}`
                : null;

        if (dateOption === 'schedule') {
            if (!schedule_date || !scheduleTime) {
                alert('Please choose a schedule date and time.');
                return;
            }
            const when = new Date(`${schedule_date}T${scheduleTime}`);
            if (isNaN(when.getTime()) || when.getTime() <= Date.now()) {
                alert('The schedule date & time must be in the future.');
                return;
            }
        }

        const broadcastData: BroadcastData = {
            broadcast_name: broadcastName.trim(),
            template_id: selectedTemplate,
            type: dateOption, // schedule or non-schedule
            ...(audience.mode === 'manual'
                ? { contacts: Array.from(audience.ids) }
                : {
                      select_all: true,
                      search: audience.search || undefined,
                      group: audience.group || undefined,
                      exclude: audience.excluded.size ? Array.from(audience.excluded) : undefined,
                  }),
            schedule_date,
            schedule_time,
        };

        try {
            setSubmitting(true);
            await CreateBroadcast(broadcastData);
            alert("Broadcast created successfully!");
            setBroadcastName("");
            setSelectedTemplate("");
            clearAudience();
            setShowPreview(false);
            setShowTemplatePreview(false);
        } catch (error: any) {
            alert(`Failed to create broadcast: ${error?.message ?? error}`);
        } finally {
            setSubmitting(false);
        }
    };

    const renderHeaderPreview = (template: Template) => {
        const type = (template.title_type || 'none').toLowerCase();
        if (!type || type === 'none') return null;

        if (type === 'text') {
            return <div className="fw-bold mb-2" style={{ fontSize: 15, color: '#111b21' }}>{template.title_text}</div>;
        }

        const isMedia = type === 'image' || type === 'video' || type === 'document';
        const sampleUrl = template.title_file
            ? `https://api.skynyx.ai/file_storage/${companyId}/template/${template.title_file}`
            : '';
        const mediaUrl = headerFileUrl || sampleUrl;
        const fileName = headerFile?.name || template.title_file || `${type} file`;
        const accept = type === 'image' ? 'image/*' : type === 'video' ? 'video/*' : '*/*';

        return (
            <div className="mb-2">
                {type === 'image' && (
                    mediaUrl ? (
                        <img src={mediaUrl} alt="Header" style={{ width: '100%', borderRadius: 8, display: 'block' }} />
                    ) : (
                        <div className="d-flex align-items-center justify-content-center" style={{ height: 150, borderRadius: 8, background: '#dfe5e7', color: '#8696a0' }}>
                            <i className="bi bi-image" style={{ fontSize: 34 }} />
                        </div>
                    )
                )}

                {type === 'video' && (
                    mediaUrl ? (
                        <video src={mediaUrl} controls style={{ width: '100%', borderRadius: 8, background: '#000', display: 'block' }} />
                    ) : (
                        <div className="d-flex align-items-center justify-content-center" style={{ height: 150, borderRadius: 8, background: '#0b141a', color: '#fff' }}>
                            <i className="bi bi-play-circle" style={{ fontSize: 34 }} />
                        </div>
                    )
                )}

                {type === 'document' && (
                    <div className="d-flex align-items-center p-2" style={{ background: '#f5f6f6', borderRadius: 8, border: '1px solid rgba(0,0,0,0.06)' }}>
                        <div className="d-flex align-items-center justify-content-center me-2 flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 8, background: '#e9412b', color: '#fff' }}>
                            <i className="bi bi-file-earmark-text" style={{ fontSize: 20 }} />
                        </div>
                        <div className="min-width-0">
                            <div className="fw-semibold text-truncate" style={{ fontSize: 13, color: '#111b21', maxWidth: 200 }}>{fileName}</div>
                            <small className="text-muted">Document</small>
                        </div>
                    </div>
                )}

                {isMedia && (
                    <label className="d-inline-flex align-items-center mt-2" style={{ cursor: 'pointer', color: '#0eb7eb', fontSize: 12, fontWeight: 600 }}>
                        <i className="bi bi-paperclip me-1" />
                        {headerFile ? 'Change file' : `Select ${type} to send`}
                        <input type="file" accept={accept} hidden onChange={onHeaderFileChange} />
                    </label>
                )}
            </div>
        );
    };

    // =================== TEMPLATE PREVIEW ===================
    const renderTemplatePreview = () => {
        const template = getSelectedTemplateDetails();
        if (!template) return null;

        return (
            <Card className="h-100 overflow-hidden">
                <Card.Header className="header-bg">
                    <h5 className="mb-0 text-white">Template Preview</h5>
                </Card.Header>

                <Card.Body className="p-0 d-flex">
                    <div className="whatsapp-preview-container">
                        <div className="whatsapp-preview">

                            {/* Chat header bar */}
                            <div className="d-flex align-items-center" style={{ background: '#008069', color: '#fff', padding: '10px 12px' }}>
                                <img
                                    src={`https://api.skynyx.ai/storage/app/public/${profile}`}
                                    onError={(e) => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }}
                                    className="rounded-circle me-2"
                                    width={40}
                                    height={40}
                                    style={{ objectFit: 'cover', background: 'rgba(255,255,255,0.2)' }}
                                    alt=""
                                />
                                <div className="min-width-0">
                                    <div className="fw-bold text-truncate">{companyName}</div>
                                    <small style={{ opacity: 0.85 }}>online</small>
                                </div>
                            </div>

                            {/* Chat wallpaper + bubble */}
                            <div style={{ background: '#efeae2', padding: '16px 14px', minHeight: 260 }}>
                                <div
                                    style={{
                                        position: 'relative',
                                        background: '#ffffff',
                                        borderRadius: 10,
                                        borderTopLeftRadius: 2,
                                        boxShadow: '0 1px 1px rgba(0,0,0,0.12)',
                                        padding: 8,
                                        maxWidth: '88%',
                                        fontSize: 14,
                                        color: '#111b21',
                                    }}
                                >
                                    {renderHeaderPreview(template)}

                                    {template.body && (
                                        <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>
                                            {template.body}
                                        </div>
                                    )}

                                    {template.footer && (
                                        <div className="mt-1" style={{ fontSize: 12, color: '#667781' }}>
                                            {template.footer}
                                        </div>
                                    )}

                                    {/* timestamp + ticks */}
                                    <div className="d-flex justify-content-end align-items-center" style={{ gap: 3, marginTop: 2 }}>
                                        <span style={{ fontSize: 11, color: '#667781' }}>
                                            {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                        <i className="bi bi-check2-all" style={{ fontSize: 14, color: '#53bdeb' }} />
                                    </div>

                                    {(template.template_buttons?.length ?? 0) > 0 && (
                                        <div style={{ marginTop: 6, borderTop: '1px solid #e9edef' }}>
                                            {template.template_buttons?.map((btn, i) => (
                                                <div
                                                    key={i}
                                                    className="text-center py-2"
                                                    style={{ color: '#0096de', fontSize: 13, fontWeight: 500, borderTop: i === 0 ? 'none' : '1px solid #e9edef' }}
                                                >
                                                    <i className="bi bi-box-arrow-up-right me-1" />{btn.text}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                        </div>
                    </div>
                </Card.Body>
            </Card>
        );
    };

    // =================== RENDER ===================
    if (loading) return <div className="text-center my-5"><Spinner animation="border" /> Loading...</div>;
    if (error) return <div className="alert alert-danger">{error}</div>;

    return (
        <>

            <Row className="g-3 justify-content-center">
                <Col xs={12} lg={7}>
                    <Card className="h-100">
                        <Card.Header className="header-bg d-flex justify-content-between align-items-center">
                            <h5 className="mb-0 text-white">Broadcast</h5>
                        </Card.Header>
                        <Card.Body>
                            <Form>
                                <Row>
                                    <Col xs={12}>

                                        {/* Broadcast Name */}
                                        <Form.Group className="mb-3">
                                            <Form.Label>Broadcast Name</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={broadcastName}
                                                onChange={(e) => setBroadcastName(e.target.value)}
                                                placeholder="Enter broadcast name"
                                            />
                                        </Form.Group>
                                        {/* Template */}
                                        <Form.Group className="mb-3">
                                            <Form.Label>Templates</Form.Label>
                                            <div className="d-flex">
                                                <Form.Select
                                                    value={selectedTemplate}
                                                    onChange={handleTemplateChange}
                                                >
                                                    <option value="">Select a template</option>
                                                    {templates.map(template => (
                                                        <option key={template.template_id} value={template.template_id}>
                                                            {template.template_name}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                                <Button
                                                    variant="outline-info"
                                                    className="ms-2"
                                                    onClick={() => setShowTemplateModal(true)}
                                                >
                                                    + New
                                                </Button>
                                            </div>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col xs={8}>

                                        {/* Contact Selection */}
                                        <Form.Group className="mb-3">
                                            <Form.Label>Contacts</Form.Label>
                                            <div className="d-flex flex-wrap gap-2 mb-3">
                                                <Button
                                                    variant={isGroupActive('') ? 'info' : 'outline-info'}
                                                    size="sm"
                                                    onClick={() => selectGroup('', totalContacts)}
                                                >
                                                    All contacts ({totalContacts.toLocaleString()})
                                                </Button>
                                                {groups.map(group => (
                                                    <Button
                                                        key={group.name}
                                                        variant={isGroupActive(group.name) ? 'info' : 'outline-info'}
                                                        size="sm"
                                                        onClick={() => selectGroup(group.name, group.total)}
                                                    >
                                                        {group.name} ({group.total.toLocaleString()})
                                                    </Button>
                                                ))}
                                            </div>
                                            <div className="d-flex align-items-center flex-wrap gap-2">
                                                <Button
                                                    variant="outline-info"
                                                    onClick={() => setShowContactModal(true)}
                                                >
                                                    View / Select Contacts
                                                </Button>
                                                <span className={selectedCount > 0 ? 'text-success fw-semibold' : 'text-muted'}>
                                                    {selectedCount > 0
                                                        ? `${selectedCount.toLocaleString()} selected (${audienceLabel})`
                                                        : 'No contacts selected'}
                                                </span>
                                                {selectedCount > 0 && (
                                                    <Button variant="link" size="sm" className="p-0" onClick={clearAudience}>
                                                        Clear
                                                    </Button>
                                                )}
                                            </div>
                                        </Form.Group>

                                        {/* Schedule Options */}
                                        {/* <div className="mb-3"> */}
                                        <Form.Group className="mb-3">
                                            <Row>
                                                <Col>
                                                    <Form.Check
                                                        inline
                                                        label="Send Now"
                                                        checked={dateOption === 'non-schedule'}
                                                        onChange={() => setDateOption('non-schedule')}
                                                    />
                                                </Col>
                                                <Col>
                                                    <Form.Check
                                                        inline
                                                        label="Schedule"
                                                        checked={dateOption === 'schedule'}
                                                        onChange={() => setDateOption('schedule')}
                                                    />
                                                </Col>
                                            </Row>
                                        </Form.Group>
                                        {/* </div> */}

                                        {/* Date & Time Inputs */}

                                        {dateOption === 'schedule' && (
                                            <Row className="mb-3">
                                                <Col md={8}>
                                                    <Form.Group>
                                                        <Form.Label>Date</Form.Label>
                                                        {/* <Form.Control
                                                            type="date"
                                                            value={scheduleDate}
                                                            onChange={(e) => setScheduleDate(e.target.value)}
                                                            min={new Date().toISOString().split('T')[0]}
                                                        /> */}
                                                        <ShreyuDatepicker
                                                            hideAddon
                                                            dateFormat="dd-MM-yyyy"
                                                            value={scheduleDate}
                                                            onChange={(date: Date) => setScheduleDate(date)}
                                                        // min={new Date().toISOString().split("T")[0]}
                                                        />


                                                    </Form.Group>
                                                </Col>
                                                <Col md={4}>
                                                    <Form.Group>
                                                        <Form.Label>Time</Form.Label>
                                                        <Form.Control
                                                            type="time"
                                                            value={scheduleTime}
                                                            onChange={(e) => setScheduleTime(e.target.value)}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                        )}

                                        {/* Submit Button */}
                                        <Button
                                            variant="success"
                                            onClick={handleSubmit}
                                            disabled={!broadcastName || !selectedTemplate || selectedCount === 0 || submitting}
                                        >
                                            {submitting ? 'Creating…' : 'Create Broadcast'}
                                        </Button>
                                    </Col>

                                    {/* Template Preview */}

                                </Row>
                            </Form>

                        </Card.Body>
                    </Card>
                </Col>

                <Col xs={12} lg={5}>
                    {showTemplatePreview && renderTemplatePreview()}
                </Col>
            </Row>

            {/* Template Modal */}
            <Modal show={showTemplateModal} onHide={() => setShowTemplateModal(false)} size="xl">
                <Modal.Header closeButton />
                <Modal.Body>
                    <AddNewTemplate />
                </Modal.Body>
            </Modal>

            {/* Contact Modal */}
            <Modal show={showContactModal} onHide={() => setShowContactModal(false)} size="lg">
                <Modal.Header className="d-flex justify-content-between align-items-center" style={{ background: 'linear-gradient(135deg, #0eb7eb, #167491)' }} >
                    <Modal.Title className='text-white'>Select Contacts</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {audience.mode === 'filter' ? (
                        <div className="alert alert-info d-flex justify-content-between align-items-center py-2">
                            <span>
                                Audience: <strong>{audienceLabel}</strong> — {selectedCount.toLocaleString()} selected.
                                Untick anyone to leave them out.
                            </span>
                            <Button variant="link" size="sm" className="p-0" onClick={clearAudience}>
                                Clear &amp; pick manually
                            </Button>
                        </div>
                    ) : (
                        <div className="d-flex gap-2 mb-3">
                            <Form.Control
                                type="text"
                                placeholder="Search by name or number..."
                                value={pickerSearchInput}
                                onChange={(e) => setPickerSearchInput(e.target.value)}
                            />
                            <Form.Select
                                style={{ maxWidth: 200 }}
                                value={pickerGroup}
                                onChange={(e) => {
                                    setPickerGroup(e.target.value);
                                    setPickerPage(1);
                                }}
                            >
                                <option value="">All groups</option>
                                {groups.map(g => (
                                    <option key={g.name} value={g.name}>
                                        {g.name} ({g.total.toLocaleString()})
                                    </option>
                                ))}
                            </Form.Select>
                        </div>
                    )}

                    {audience.mode === 'manual' && allOnPageChecked && pickerTotal > pickerRows.length && (
                        <div className="alert alert-light border text-center py-2 mb-2">
                            All {pickerRows.length} contacts on this page are selected.{' '}
                            <Button variant="link" size="sm" className="p-0 align-baseline" onClick={selectAllMatching}>
                                Select all {pickerTotal.toLocaleString()} contacts
                                {pickerSearch || pickerGroup ? ' matching this search' : ''}
                            </Button>
                        </div>
                    )}

                    <div style={{ opacity: pickerLoading && pickerRows.length ? 0.55 : 1, transition: 'opacity .15s', minHeight: 200 }}>
                        <Table striped bordered hover size="sm">
                            <thead>
                                <tr>
                                    <th style={{ width: 40 }}>
                                        <Form.Check
                                            type="checkbox"
                                            checked={allOnPageChecked}
                                            onChange={(e) => togglePage(e.target.checked)}
                                        />
                                    </th>
                                    <th>Name</th>
                                    <th>Phone Number</th>
                                    <th>Group</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pickerRows.map(contact => (
                                    <tr key={contact.contact_id}>
                                        <td>
                                            <Form.Check
                                                type="checkbox"
                                                checked={isChecked(contact.contact_id)}
                                                onChange={() => toggleContact(contact.contact_id)}
                                            />
                                        </td>
                                        <td>{contact.contact_name}</td>
                                        <td>{contact.mobile_no}</td>
                                        <td>{contact.group_name}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                        {!pickerLoading && pickerRows.length === 0 && (
                            <div className="text-center text-muted py-4">No contacts found</div>
                        )}
                    </div>

                    {/* Pagination Controls */}
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled={pickerPage === 1 || pickerLoading}
                            onClick={() => setPickerPage(prev => prev - 1)}
                        >
                            Previous
                        </Button>
                        <span>
                            Page {pickerPage.toLocaleString()} of {pickerLastPage.toLocaleString()}
                            <span className="text-muted"> · {pickerTotal.toLocaleString()} contacts</span>
                        </span>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled={pickerPage === pickerLastPage || pickerLoading}
                            onClick={() => setPickerPage(prev => prev + 1)}
                        >
                            Next
                        </Button>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <span className="me-auto text-muted">{selectedCount.toLocaleString()} selected</span>
                    <Button variant="secondary" onClick={() => setShowContactModal(false)}>
                        Close
                    </Button>
                    <Button variant="primary" onClick={() => setShowContactModal(false)}>
                        Save Selection
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default Broadcast;